import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {FundAiSetupActions} from "../store/fund-ai-setup.actions";
import {AiSetupOverwriteMode, AiSetupSection} from "../../fund/generated-sources/api";
import {GptApiService, WebSearchResultItem} from "../../shared/generated-sources/api/gpt";
import {AssetApiService} from "../../asset/generated-sources/api";
import {MatSnackBar} from "@angular/material/snack-bar";
import {catchError, combineLatest, finalize, forkJoin, of, take} from "rxjs";
import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {selectSelectedFundReportId} from "../../fund/store/fund-report/fund-report.selectors";
import {selectSelectedFundValuationId} from "../../fund/store/fund-valuation/fund-valuation.selectors";

export interface AssetOption {
    id: number;
    name: string;
    isHeld: boolean;
}

export interface FundAiSetupDialogData {
    fundId: number;
    assets: AssetOption[];
}

interface SectionOption {
    value: AiSetupSection;
    label: string;
}

@Component({
    selector: "valumize-fund-ai-setup-dialog",
    templateUrl: "./fund-ai-setup-dialog.component.html",
    styleUrls: ["./fund-ai-setup-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FundAiSetupDialogComponent {

    readonly data = inject<FundAiSetupDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<FundAiSetupDialogComponent>>(MatDialogRef);
    private readonly fb = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly gptApi = inject(GptApiService);
    private readonly assetApi = inject(AssetApiService);
    private readonly snack = inject(MatSnackBar);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly sections: SectionOption[] = [
        {value: AiSetupSection.CompanyDescription, label: "Company description"},
        {value: AiSetupSection.EndMarkets, label: "End markets & customers"},
        {value: AiSetupSection.Swot, label: "SWOT"},
        {value: AiSetupSection.GeneralInfo, label: "General info"},
        {value: AiSetupSection.ComparableCompanies, label: "Comparable companies"}
    ];

    readonly overwriteModes: { value: AiSetupOverwriteMode; label: string }[] = [
        {value: AiSetupOverwriteMode.FillBlanksOnly, label: "Fill blanks only"},
        {value: AiSetupOverwriteMode.RegenerateAiFields, label: "Re-generate AI-sourced fields, keep manual edits"},
        {value: AiSetupOverwriteMode.RegenerateAll, label: "Re-generate everything (overwrites manual edits)"}
    ];

    readonly form = this.fb.group({
        assetIds: this.fb.control<number[]>(
            this.data.assets.filter(a => a.isHeld).map(a => a.id),
            Validators.required
        ),
        sections: this.fb.control<AiSetupSection[]>(
            this.sections.map(s => s.value),
            Validators.required
        ),
        overwriteMode: this.fb.control<AiSetupOverwriteMode>(
            AiSetupOverwriteMode.FillBlanksOnly,
            Validators.required
        ),
        contextNote: this.fb.control<string>("")
    });

    /** Per-asset URL state, keyed by assetId. Empty string = use whatever's on the asset already. */
    urls = new Map<number, string>();

    /** Per-asset "Finding URL…" indicator while WebSearchService is in flight. */
    urlSearching = new Set<number>();

    /** Per-asset search results to pick from. Populated after Find returns; cleared on pick. */
    urlSuggestions = new Map<number, WebSearchResultItem[]>();

    constructor() {
        for (const asset of this.data.assets) {
            this.urls.set(asset.id, "");
        }
        // Pre-fetch existing URLs in parallel so the user sees what's already on file.
        // Errors per-asset are swallowed (treated as "no current URL") — Find / type still works.
        const lookups = this.data.assets.map(a =>
            this.assetApi.getAsset(a.id).pipe(catchError(() => of(null)))
        );
        forkJoin(lookups).subscribe(assets => {
            assets.forEach((asset, idx) => {
                const url = asset?.url?.text ?? "";
                if (url.trim()) {
                    this.urls.set(this.data.assets[idx].id, url);
                }
            });
            this.cdr.markForCheck();
        });
    }

    selectHeldOnly() {
        this.form.controls.assetIds.setValue(
            this.data.assets.filter(a => a.isHeld).map(a => a.id)
        );
    }

    selectAll() {
        this.form.controls.assetIds.setValue(this.data.assets.map(a => a.id));
    }

    clearAll() {
        this.form.controls.assetIds.setValue([]);
    }

    isAssetSelected(assetId: number): boolean {
        return (this.form.controls.assetIds.value ?? []).includes(assetId);
    }

    toggleAsset(assetId: number, checked: boolean) {
        const current = this.form.controls.assetIds.value ?? [];
        const next = checked ? [...current, assetId] : current.filter(id => id !== assetId);
        this.form.controls.assetIds.setValue(next);
    }

    isSectionSelected(section: AiSetupSection): boolean {
        return (this.form.controls.sections.value ?? []).includes(section);
    }

    toggleSection(section: AiSetupSection, checked: boolean) {
        const current = this.form.controls.sections.value ?? [];
        const next = checked ? [...current, section] : current.filter(s => s !== section);
        this.form.controls.sections.setValue(next);
    }

    onUrlInput(assetId: number, value: string) {
        this.urls.set(assetId, value);
    }

    getUrl(assetId: number): string {
        return this.urls.get(assetId) ?? "";
    }

    isSearching(assetId: number): boolean {
        return this.urlSearching.has(assetId);
    }

    /**
     * Searches URLs in parallel for every currently-selected asset that doesn't already
     * have a URL typed in. Each search updates its row independently as it finishes.
     */
    findAllMissing() {
        const selectedIds = new Set<number>(this.form.controls.assetIds.value ?? []);
        const targets = this.data.assets.filter(a =>
            selectedIds.has(a.id) && !this.getUrl(a.id).trim() && !this.urlSearching.has(a.id)
        );
        if (targets.length === 0) {
            this.snack.open("Nothing to search — all selected assets already have a URL typed in", "OK", {duration: 3000});
            return;
        }
        for (const asset of targets) {
            this.findUrl(asset);
        }
    }

    findUrl(asset: AssetOption) {
        if (this.urlSearching.has(asset.id)) return;
        this.urlSearching.add(asset.id);
        this.urlSuggestions.delete(asset.id);
        this.cdr.markForCheck();
        this.gptApi.searchWebsite(`${asset.name} official website`, 5)
            .pipe(finalize(() => {
                this.urlSearching.delete(asset.id);
                this.cdr.markForCheck();
            }))
            .subscribe({
                next: result => {
                    const items = result.results ?? [];
                    if (items.length === 0) {
                        this.snack.open(`No URL found for "${asset.name}" — type one manually`, "OK", {duration: 4000});
                        return;
                    }
                    this.urlSuggestions.set(asset.id, items);
                    this.cdr.markForCheck();
                },
                error: err => {
                    console.error("URL search failed", err);
                    this.snack.open(`URL search failed: ${ErrorHandlingUtil.getErrorMessage(err)}`, "OK", {duration: 5000});
                }
            });
    }

    pickSuggestion(asset: AssetOption, item: WebSearchResultItem) {
        this.urls.set(asset.id, item.url);
        this.urlSuggestions.delete(asset.id);
        this.cdr.markForCheck();
    }

    dismissSuggestions(assetId: number) {
        this.urlSuggestions.delete(assetId);
        this.cdr.markForCheck();
    }

    getSuggestions(assetId: number): WebSearchResultItem[] {
        return this.urlSuggestions.get(assetId) ?? [];
    }

    submit() {
        if (this.form.invalid) return;
        const v = this.form.value;
        const selectedIds = new Set<number>(v.assetIds ?? []);
        const urlOverrides = Array.from(this.urls.entries())
            .filter(([assetId, url]) => selectedIds.has(assetId) && url.trim().length > 0)
            .map(([assetId, url]) => ({assetId, url: url.trim()}));
        // Build the originating route from the currently-selected report/valuation in the
        // store. The router URL is just /funds/{id} — which report/valuation is being
        // viewed lives in NgRx, not the URL — so we synthesize the deep link from store
        // state. `fundReportId` / `fundValuationId` are the same query-param keys the fund
        // detail page's NavigationEnd→reloadPage handler already reads on entry.
        // Server validates the URL stays within /funds/{fundId}.
        combineLatest([
            this.store.select(selectSelectedFundReportId),
            this.store.select(selectSelectedFundValuationId)
        ]).pipe(take(1)).subscribe(([fundReportId, fundValuationId]) => {
            const params: string[] = [];
            if (fundReportId) params.push(`fundReportId=${fundReportId}`);
            if (fundValuationId) params.push(`fundValuationId=${fundValuationId}`);
            const returnUrl = `/funds/${this.data.fundId}${params.length ? "?" + params.join("&") : ""}`;
            this.store.dispatch(FundAiSetupActions.start({
                fundId: this.data.fundId,
                request: {
                    assetIds: v.assetIds!,
                    sections: v.sections!,
                    overwriteMode: v.overwriteMode!,
                    contextNote: v.contextNote || undefined,
                    urlOverrides: urlOverrides.length > 0 ? urlOverrides : undefined,
                    returnUrl
                }
            }));
            this.dialogRef.close();
        });
    }

    cancel() {
        this.dialogRef.close();
    }
}
